import { useMemo, useState, useCallback } from "react";
import { BestValueTransition } from "@/components/BestValueTransition";
import { useBranding } from "@/data/BrandingContext";
import { useProductOptions } from "@/data/ProductOptionsContext";
import { usePricing } from "@/data/PricingContext";
import { deriveBuildSummary, derivePricing, deriveScope, createConfigSnapshot, type BuildSummaryItem } from "@/data/buildConfigMapping";
import type { PricingOverrides } from "@/data/pricingEngine";
import type { Quote, ContractDocument } from "@/data/quoteTypes";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SignatureCapture } from "@/components/SignatureCapture";
import { Checkbox } from "@/components/ui/checkbox";
import {
  X, Phone, Mail, MapPin, ShowerHead, Layers, GlassWater,
  DoorOpen, Wrench, Grip, Sparkles, Palette, RectangleHorizontal,
  ChevronRight, Check, Eye, CreditCard, Banknote, CalendarCheck,
  Shield, CheckCircle, FileText, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quote: Quote;
  onContractApproved?: (contract: ContractDocument) => void;
  onScheduleFollowUp?: () => void;
}

/* ═══════════════════════════════════════════ */
/*  Stages: pricing reveal → close layer       */
/* ═══════════════════════════════════════════ */
type Stage =
  | "hidden" | "transition" | "standard" | "promo" | "ivp"
  | "proceed" | "payment" | "deposit" | "contract" | "confirmed";

const PRICING_STAGES: Stage[] = ["hidden", "transition", "standard", "promo", "ivp"];
const CLOSE_STAGES: Stage[] = ["proceed", "payment", "deposit", "contract", "confirmed"];
const ALL_STAGES: Stage[] = [...PRICING_STAGES, ...CLOSE_STAGES];

/* ═══════════════════════════════════════════ */
/*  Icon mapping for build items               */
/* ═══════════════════════════════════════════ */
const ITEM_ICONS: Record<string, React.ElementType> = {
  System: ShowerHead, Base: RectangleHorizontal, "Base Size": RectangleHorizontal,
  "Base Type": RectangleHorizontal, "Base Style": RectangleHorizontal,
  Material: Palette, "Wall System": Layers, Glass: GlassWater,
  Door: DoorOpen, Fixtures: Wrench, "Grab Bars": Grip, "Add-ons": Sparkles,
  "Wall Color": Palette, "Base Color": Palette, "Color Selection": Palette,
};

function getItemIcon(label: string): React.ElementType {
  return ITEM_ICONS[label] || CheckCircle;
}

export function CustomerQuoteView({ open, onOpenChange, quote, onContractApproved, onScheduleFollowUp }: Props) {
  const { branding } = useBranding();
  const { options: allOptions } = useProductOptions();
  const { config: pricingConfig } = usePricing();
  const [stage, setStage] = useState<Stage>("hidden");

  // Close layer state
  const [selectedPayment, setSelectedPayment] = useState<"cash" | "financing" | null>(null);
  const [homeownerSig, setHomeownerSig] = useState("");
  const [repSig, setRepSig] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [rescissionAcknowledged, setRescissionAcknowledged] = useState(false);

  const brandColor = `hsl(${branding.primaryHue} ${branding.primarySaturation}% ${branding.primaryLightness}%)`;
  const brandColorLight = `hsl(${branding.primaryHue} ${branding.primarySaturation}% ${branding.primaryLightness}% / 0.08)`;
  const brandColorMid = `hsl(${branding.primaryHue} ${branding.primarySaturation}% ${branding.primaryLightness}% / 0.15)`;

  const pricingOverrides: PricingOverrides = {
    systemBasePrices: pricingConfig.systemBasePrices,
    dimensionPresets: pricingConfig.dimensionPresets,
    materialMultipliers: pricingConfig.materialMultipliers,
    complexityAdders: pricingConfig.complexityAdders,
  };

  /* ── All derived from single source: projectConfig ── */
  const pricing = useMemo(() => {
    if (!quote.projectConfig) return null;
    return derivePricing(quote.projectConfig, allOptions, pricingOverrides);
  }, [quote.projectConfig, allOptions, pricingOverrides]);

  const scope = useMemo(() => {
    if (!quote.projectConfig) return null;
    return deriveScope(quote.projectConfig);
  }, [quote.projectConfig]);

  const buildItems: BuildSummaryItem[] = useMemo(() => {
    if (!quote.projectConfig) return [];
    return deriveBuildSummary(quote.projectConfig, allOptions);
  }, [quote.projectConfig, allOptions]);

  const total = pricing?.standardPrice || quote.totalEstimate;
  const promoPrice = pricing?.promoPrice || Math.round(total * 0.90);
  const ivpCashPrice = pricing?.ivpCashPrice || Math.round(total * 0.85);
  const ivpFinancePrice = pricing?.ivpFinancePrice || Math.round(total * 0.95);

  const chosenPrice = selectedPayment === "cash" ? ivpCashPrice : selectedPayment === "financing" ? ivpFinancePrice : total;
  const depositAmount = selectedPayment === "financing" ? 0 : Math.round(chosenPrice * 0.5);

  const advance = useCallback((to?: Stage) => {
    if (to) { setStage(to); return; }
    const idx = ALL_STAGES.indexOf(stage);
    if (idx < ALL_STAGES.length - 1) setStage(ALL_STAGES[idx + 1]);
  }, [stage]);

  const goBack = useCallback(() => {
    const idx = ALL_STAGES.indexOf(stage);
    if (idx > 0) setStage(ALL_STAGES[idx - 1]);
  }, [stage]);

  const resetAndClose = useCallback(() => {
    setStage("hidden");
    setSelectedPayment(null);
    setHomeownerSig("");
    setRepSig("");
    setTermsAccepted(false);
    setRescissionAcknowledged(false);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleSignContract = useCallback(() => {
    const snapshot = quote.projectConfig
      ? createConfigSnapshot(quote.projectConfig)
      : { hash: "", snapshotAt: new Date().toISOString() };
    const now = new Date().toISOString();
    const contract: ContractDocument = {
      id: quote?.id ? `CTR-${quote.id.replace("QT-", "")}` : `CTR-000`,
      quoteId: quote.id,
      customerName: quote.customerName,
      customerEmail: quote.customerEmail,
      customerAddress: quote.customerAddress,
      contractTotal: chosenPrice,
      homeownerSignature: homeownerSig,
      homeownerSignedAt: now,
      repSignature: repSig,
      repSignedAt: now,
      termsAccepted: true,
      rescissionAcknowledged: true,
      status: "Signed",
      createdAt: now,
      approvedAt: now,
      ownershipType: quote.verification?.ownershipType === "POA" ? "POA" : "Homeowner",
      poaDocumentUploaded: quote.verification?.poaDocumentUploaded || false,
      hoaInvolved: quote.verification?.hoaInvolved || false,
      selectedOptionsApproved: true,
      configSnapshotHash: snapshot.hash,
      configSnapshotAt: snapshot.snapshotAt,
      paymentMethod: selectedPayment === "financing" ? "Financing" : "Cash",
      depositAmount,
    };
    onContractApproved?.(contract);
    toast.success("Contract signed successfully!");
    setStage("confirmed");
  }, [quote, chosenPrice, homeownerSig, repSig, onContractApproved, selectedPayment, depositAmount]);

  const canSign = homeownerSig && repSig && termsAccepted && rescissionAcknowledged;
  const isCloseLayer = CLOSE_STAGES.includes(stage);
  const stageIndex = ALL_STAGES.indexOf(stage);

  const pricingDotCount = PRICING_STAGES.length - 1;
  const closeDotCount = CLOSE_STAGES.length;
  const pricingProgress = Math.min(stageIndex, pricingDotCount);
  const closeProgress = isCloseLayer ? ALL_STAGES.indexOf(stage) - PRICING_STAGES.length + 1 : 0;

  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0 gap-0">
        <DialogHeader>
          <DialogTitle className="sr-only">Quote Presentation</DialogTitle>
        </DialogHeader>
        {/* <button onClick={resetAndClose} className="absolute right-4 top-4 z-10 p-1.5 rounded-full hover:bg-muted transition-colors">
          <X className="h-4 w-4" />
        </button> */}

        {/* ═══ Header / Branding ═══ */}
        <div className="px-8 pt-8 pb-5 border-b" style={{ background: `linear-gradient(135deg, ${brandColorLight}, transparent)` }}>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight" style={{ color: brandColor }}>{branding.companyName}</h1>
              <p className="text-sm text-muted-foreground mt-0.5">{branding.tagline}</p>
            </div>
            <div className="text-right space-y-0.5">
              <p className="text-xs text-muted-foreground">Quote #{quote.displayReference || quote.id}</p>
              {/* <p className="text-xs text-muted-foreground">{quote.createdAt}</p> */}
              <p className="text-xs text-muted-foreground">
                {quote.createdAt ? new Date(quote.createdAt).toLocaleString() : ""}
              </p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-border/40">
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Prepared For</p>
            <h2 className="text-lg font-bold">{quote.customerName}</h2>
            <div className="flex flex-wrap gap-4 mt-1 text-sm text-muted-foreground">
              {quote.customerAddress && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{quote.customerAddress}</span>}
              {quote.customerPhone && <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{quote.customerPhone}</span>}
              {quote.customerEmail && <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{quote.customerEmail}</span>}
            </div>
          </div>
        </div>

        {/* ═══ Body: Build Summary (left) + Pricing/Close (right) ═══ */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">

          {/* ─── A. Project Overview — derived from projectConfig ─── */}
          <div className="px-8 py-6 md:border-r border-b md:border-b-0">
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.15em] mb-4">Your Project</p>
            {scope?.summary && <p className="text-sm font-medium mb-5 leading-relaxed">{scope.summary}</p>}
            <div className="space-y-2.5">
              {buildItems.map(item => {
                const Icon = getItemIcon(item.label);
                return (
                  <div key={item.label} className="flex items-start gap-3 p-2.5 rounded-lg" style={{ background: brandColorLight }}>
                    <div className="mt-0.5 p-1.5 rounded-md shrink-0" style={{ background: brandColorMid }}>
                      <Icon className="h-3.5 w-3.5" style={{ color: brandColor }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{item.label}</p>
                      <p className="text-sm font-semibold mt-0.5 leading-snug">{item.value}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            {scope && scope.sections.length > 0 && (
              <div className="mt-6 pt-5 border-t">
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.15em] mb-3">What's Included</p>
                <div className="space-y-3">
                  {scope.sections.map((section, i) => (
                    <div key={i}>
                      <p className="text-xs font-semibold mb-1">{section.title}</p>
                      <ul className="space-y-0.5">
                        {section.items.map((item, j) => (
                          <li key={j} className="text-xs text-muted-foreground flex items-start gap-2">
                            <Check className="h-3 w-3 mt-0.5 shrink-0" style={{ color: brandColor }} />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ─── B. Pricing + Close Layer — derived from projectConfig ─── */}
          <div className="px-8 py-6 flex flex-col min-h-[400px]">
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.15em] mb-4">
              {isCloseLayer ? "Let's Get Started" : "Investment"}
            </p>

            <div className="flex-1 flex flex-col justify-center">

              {/* ══════ PRICING STAGES ══════ */}

              {stage === "hidden" && (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4" style={{ background: brandColorMid }}>
                    <Eye className="h-7 w-7" style={{ color: brandColor }} />
                  </div>
                  <p className="text-sm text-muted-foreground mb-6">Ready to present pricing</p>
                  <Button size="lg" className="h-14 px-10 text-base font-semibold rounded-xl shadow-lg" style={{ background: brandColor }} onClick={() => advance()}>
                    Reveal Standard Price <ChevronRight className="h-5 w-5 ml-1" />
                  </Button>
                </div>
              )}

              {stage === "transition" && (
                <BestValueTransition brandColor={brandColor} brandColorLight={brandColorLight} brandColorMid={brandColorMid} companyName={branding.companyName} onComplete={() => advance()} />
              )}

              {stage === "standard" && (
                <div className="text-center py-8 animate-in fade-in-0 slide-in-from-bottom-4 duration-500">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Standard Price</p>
                  <p className="text-[10px] text-muted-foreground mb-3">Valid for 1 year from date of quote</p>
                  <p className="text-5xl font-bold tracking-tight" style={{ color: brandColor }}>${total.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground mt-3">Complete project as quoted</p>
                  <Button size="lg" className="mt-8 h-14 px-10 text-base font-semibold rounded-xl shadow-lg" style={{ background: brandColor }} onClick={() => advance()}>
                    See Today's Promotion <ChevronRight className="h-5 w-5 ml-1" />
                  </Button>
                </div>
              )}

              {stage === "promo" && (
                <div className="text-center py-8 animate-in fade-in-0 slide-in-from-bottom-4 duration-500">
                  <p className="text-xs text-muted-foreground line-through mb-1">${total.toLocaleString()}</p>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Today's Promotional Price</p>
                  <p className="text-5xl font-bold tracking-tight" style={{ color: brandColor }}>${promoPrice.toLocaleString()}</p>
                  <div className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: brandColorMid, color: brandColor }}>
                    <Sparkles className="h-3.5 w-3.5" /> Save ${(total - promoPrice).toLocaleString()}
                  </div>
                  <Button size="lg" className="mt-8 h-14 px-10 text-base font-semibold rounded-xl shadow-lg" style={{ background: brandColor }} onClick={() => advance()}>
                    Initial Visit Program <ChevronRight className="h-5 w-5 ml-1" />
                  </Button>
                </div>
              )}

              {stage === "ivp" && (
                <div className="py-6 animate-in fade-in-0 slide-in-from-bottom-4 duration-500">
                  <div className="text-center mb-6">
                    <p className="text-xs text-muted-foreground line-through">${total.toLocaleString()}</p>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.15em] mt-1">Initial Visit Program</p>
                  </div>
                  <div className="rounded-xl p-5 mb-3 border-2 relative" style={{ borderColor: brandColor, background: brandColorLight }}>
                    <div className="absolute -top-2.5 left-4 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: brandColor, color: "white" }}>Best Value</div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-1">Cash / Check Price</p>
                    <p className="text-4xl font-bold tracking-tight mt-1" style={{ color: brandColor }}>${ivpCashPrice.toLocaleString()}</p>
                    <div className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold" style={{ color: brandColor }}>
                      <Sparkles className="h-3.5 w-3.5" /> Save ${(total - ivpCashPrice).toLocaleString()} off standard
                    </div>
                  </div>
                  <div className="rounded-xl p-5 border bg-card">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Finance Option</p>
                    <p className="text-3xl font-bold tracking-tight mt-1">${ivpFinancePrice.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground mt-1.5">$0 down · Low monthly payments available</p>
                  </div>
                  <Button size="lg" className="mt-6 w-full h-14 text-base font-semibold rounded-xl shadow-lg" style={{ background: brandColor }} onClick={() => advance()}>
                    Proceed with Project <ChevronRight className="h-5 w-5 ml-1" />
                  </Button>
                </div>
              )}

              {/* ══════ CLOSE LAYER STAGES ══════ */}

              {stage === "proceed" && (
                <div className="text-center py-8 animate-in fade-in-0 slide-in-from-bottom-4 duration-500">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-5" style={{ background: brandColorMid }}>
                    <CheckCircle className="h-8 w-8" style={{ color: brandColor }} />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Great Choice</h3>
                  <p className="text-sm text-muted-foreground mb-8 max-w-xs mx-auto leading-relaxed">
                    Let's finalize the details so we can get your project scheduled.
                  </p>
                  <Button size="lg" className="h-14 px-10 text-base font-semibold rounded-xl shadow-lg" style={{ background: brandColor }} onClick={() => advance()}>
                    Select Payment Option <ChevronRight className="h-5 w-5 ml-1" />
                  </Button>
                  <button onClick={goBack} className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 mx-auto">
                    <ArrowLeft className="h-3 w-3" /> Back to pricing
                  </button>
                </div>
              )}

              {stage === "payment" && (
                <div className="py-6 animate-in fade-in-0 slide-in-from-bottom-4 duration-500">
                  <h3 className="text-lg font-bold text-center mb-6">How Would You Like to Pay?</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => { setSelectedPayment("cash"); advance(); }}
                      className="w-full rounded-xl p-5 border-2 text-left transition-all hover:shadow-md active:scale-[0.99]"
                      style={{ borderColor: brandColor, background: brandColorLight }}
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-3 rounded-xl shrink-0" style={{ background: brandColorMid }}>
                          <Banknote className="h-6 w-6" style={{ color: brandColor }} />
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-base">Cash / Check</p>
                          <p className="text-xs text-muted-foreground mt-0.5">Best Value Today</p>
                        </div>
                        <p className="text-2xl font-bold" style={{ color: brandColor }}>${ivpCashPrice.toLocaleString()}</p>
                      </div>
                    </button>
                    <button
                      onClick={() => { setSelectedPayment("financing"); advance(); }}
                      className="w-full rounded-xl p-5 border-2 border-border text-left transition-all hover:shadow-md hover:border-muted-foreground/30 active:scale-[0.99]"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-3 rounded-xl bg-muted shrink-0">
                          <CreditCard className="h-6 w-6 text-muted-foreground" />
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-base">Financing</p>
                          <p className="text-xs text-muted-foreground mt-0.5">Flexible Monthly Option</p>
                        </div>
                        <p className="text-2xl font-bold">${ivpFinancePrice.toLocaleString()}</p>
                      </div>
                    </button>
                  </div>
                  <button onClick={goBack} className="mt-5 text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 mx-auto">
                    <ArrowLeft className="h-3 w-3" /> Back
                  </button>
                  {onScheduleFollowUp && (
                    <button
                      onClick={() => { resetAndClose(); onScheduleFollowUp(); }}
                      className="mt-3 text-xs text-warning hover:text-warning/80 transition-colors flex items-center gap-1 mx-auto"
                    >
                      Not ready today? Schedule a follow-up instead
                    </button>
                  )}
                </div>
              )}

              {stage === "deposit" && (
                <div className="py-6 animate-in fade-in-0 slide-in-from-bottom-4 duration-500">
                  <div className="text-center mb-6">
                    <h3 className="text-lg font-bold mb-1">
                      {selectedPayment === "financing" ? "No Deposit Required" : "Getting Started"}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {selectedPayment === "financing"
                        ? "Your financing option includes $0 down"
                        : "A 50% deposit secures your project and scheduling priority"}
                    </p>
                  </div>
                  <div className="rounded-xl border-2 p-6 text-center mb-6" style={{ borderColor: brandColor, background: brandColorLight }}>
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">
                      {selectedPayment === "financing" ? "Amount Due Today" : "Deposit to Get Started"}
                    </p>
                    <p className="text-4xl font-bold" style={{ color: brandColor }}>
                      {depositAmount === 0 ? "$0" : `$${depositAmount.toLocaleString()}`}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Project Total: ${chosenPrice.toLocaleString()} · {selectedPayment === "financing" ? "Financing" : "Cash / Check"}
                    </p>
                  </div>
                  <Button size="lg" className="w-full h-14 text-base font-semibold rounded-xl shadow-lg" style={{ background: brandColor }} onClick={() => advance()}>
                    Continue to Agreement <ChevronRight className="h-5 w-5 ml-1" />
                  </Button>
                  <button onClick={goBack} className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 mx-auto">
                    <ArrowLeft className="h-3 w-3" /> Change payment option
                  </button>
                  {onScheduleFollowUp && (
                    <button
                      onClick={() => { resetAndClose(); onScheduleFollowUp(); }}
                      className="mt-3 text-xs text-warning hover:text-warning/80 transition-colors flex items-center gap-1 mx-auto"
                    >
                      Not ready today? Schedule a follow-up instead
                    </button>
                  )}
                </div>
              )}

              {stage === "contract" && (
                <div className="py-4 animate-in fade-in-0 slide-in-from-bottom-4 duration-500">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="h-4 w-4" style={{ color: brandColor }} />
                    <h3 className="text-base font-bold">Agreement</h3>
                  </div>
                  <div className="rounded-lg border bg-muted/20 p-3 mb-4 text-xs space-y-1.5">
                    <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span className="font-medium">{quote.customerName}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Project Total</span><span className="font-bold" style={{ color: brandColor }}>${chosenPrice.toLocaleString()}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Payment</span><span className="font-medium">{selectedPayment === "financing" ? "Financing" : "Cash / Check"}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Due Today</span><span className="font-medium">{depositAmount === 0 ? "$0 Down" : `$${depositAmount.toLocaleString()}`}</span></div>
                  </div>
                  <div className="space-y-2.5 mb-4">
                    <div className="flex items-start gap-3 p-2.5 rounded-lg border">
                      <Checkbox id="pres-terms" checked={termsAccepted} onCheckedChange={(c) => setTermsAccepted(!!c)} />
                      <label htmlFor="pres-terms" className="text-[11px] leading-relaxed cursor-pointer">
                        I agree to the terms and conditions and authorize {branding.companyName} to perform the work at the stated price.
                      </label>
                    </div>
                    <div className="flex items-start gap-3 p-2.5 rounded-lg border">
                      <Checkbox id="pres-rescission" checked={rescissionAcknowledged} onCheckedChange={(c) => setRescissionAcknowledged(!!c)} />
                      <label htmlFor="pres-rescission" className="text-[11px] leading-relaxed cursor-pointer">
                        I acknowledge the 3-business-day right of rescission.
                      </label>
                    </div>
                  </div>
                  <div className="space-y-3 mb-4">
                    <div>
                      <SignatureCapture label="Homeowner Signature" onCapture={setHomeownerSig} existingSignature={homeownerSig} />
                      {homeownerSig && <p className="text-[10px] text-success flex items-center gap-1 mt-1"><CheckCircle className="h-3 w-3" /> Captured</p>}
                    </div>
                    <div>
                      <SignatureCapture label="Company Representative" onCapture={setRepSig} existingSignature={repSig} />
                      {repSig && <p className="text-[10px] text-success flex items-center gap-1 mt-1"><CheckCircle className="h-3 w-3" /> Captured</p>}
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-[10px] text-muted-foreground bg-muted/30 p-2.5 rounded-lg mb-4">
                    <Shield className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>Contract documents are securely stored and tied to your customer profile.</span>
                  </div>
                  <Button
                    size="lg"
                    className="w-full h-14 text-base font-semibold rounded-xl shadow-lg gap-2"
                    style={{ background: canSign ? brandColor : undefined }}
                    disabled={!canSign}
                    onClick={handleSignContract}
                  >
                    <CheckCircle className="h-5 w-5" /> Sign & Approve Agreement
                  </Button>
                  <button onClick={goBack} className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 mx-auto">
                    <ArrowLeft className="h-3 w-3" /> Back
                  </button>
                </div>
              )}

              {stage === "confirmed" && (
                <div className="text-center py-10 animate-in fade-in-0 slide-in-from-bottom-4 duration-500">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-6" style={{ background: brandColorMid }}>
                    <CheckCircle className="h-10 w-10" style={{ color: brandColor }} />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">You're All Set!</h3>
                  <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto leading-relaxed">
                    Your project is confirmed. Our team will reach out shortly to schedule your installation.
                  </p>
                  <div className="rounded-xl border p-5 max-w-xs mx-auto text-left space-y-3 mb-6">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">What Happens Next</p>
                    <div className="flex items-start gap-3">
                      <CalendarCheck className="h-4 w-4 mt-0.5 shrink-0" style={{ color: brandColor }} />
                      <p className="text-xs">We'll contact you within 24–48 hours to schedule your installation date.</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <FileText className="h-4 w-4 mt-0.5 shrink-0" style={{ color: brandColor }} />
                      <p className="text-xs">A copy of your agreement will be sent to your email on file.</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <Phone className="h-4 w-4 mt-0.5 shrink-0" style={{ color: brandColor }} />
                      <p className="text-xs">Questions? Call us anytime — we're here to help.</p>
                    </div>
                  </div>
                  <p className="text-lg font-bold" style={{ color: brandColor }}>Thank you for choosing {branding.companyName}!</p>
                </div>
              )}
            </div>

            {/* Stage indicator */}
            <div className="flex items-center justify-center gap-1.5 mt-4 pt-4 border-t">
              {Array.from({ length: pricingDotCount }).map((_, i) => (
                <div
                  key={`p-${i}`}
                  className="h-1.5 rounded-full transition-all duration-300"
                  style={{
                    width: pricingProgress >= i + 1 ? 20 : 6,
                    background: pricingProgress >= i + 1 ? brandColor : "hsl(var(--muted))",
                  }}
                />
              ))}
              {isCloseLayer && <div className="w-px h-3 bg-border mx-1" />}
              {isCloseLayer && Array.from({ length: closeDotCount }).map((_, i) => (
                <div
                  key={`c-${i}`}
                  className="h-1.5 rounded-full transition-all duration-300"
                  style={{
                    width: closeProgress >= i + 1 ? 20 : 6,
                    background: closeProgress >= i + 1 ? brandColor : "hsl(var(--muted))",
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* ═══ Footer ═══ */}
        <div className="px-8 py-4 bg-muted/20 border-t">
          <p className="text-[10px] text-muted-foreground text-center">
            This quote is valid for 30 days from the date above. Pricing is subject to change based on final measurements and site conditions.
          </p>
          <p className="text-[10px] text-muted-foreground text-center mt-1 font-medium">
            {branding.companyName} · {branding.tagline}
          </p>
          <p className="text-[8px] text-muted-foreground/40 text-center mt-1.5">Powered by {branding.platformName}</p>
        </div>

        {/* Print / Close */}
        {stage !== "confirmed" && (
          <div className="px-8 py-3 flex justify-center gap-3 print:hidden border-t">
            <Button variant="outline" size="sm" onClick={() => window.print()} className="gap-1.5 text-xs">Print / Save as PDF</Button>
            <Button variant="outline" size="sm" onClick={resetAndClose} className="text-xs">Close</Button>
          </div>
        )}
        {stage === "confirmed" && (
          <div className="px-8 py-3 flex justify-center print:hidden border-t">
            <Button variant="outline" size="sm" onClick={resetAndClose} className="text-xs">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
